import { useState } from "react";
import { Link } from "react-router-dom";
import { Check, Sparkles, ArrowUpRight, Zap, Stethoscope } from "lucide-react";
import SectionHeader from "@/components/SectionHeader";

const PLANS = [
  {
    id: "clinic",
    name: "Clinic",
    tagline: "For single-doctor & small clinic setups",
    price_m: 4999, price_y: 49990,
    highlight: false,
    features: [
      "1 Branch · 1 Department · 2 Admins · up to 5 Users",
      "OPD, e-prescriptions, billing",
      "Patient app + Health ID",
      "WhatsApp notifications",
      "Standard digital reports",
      "Email support",
    ],
    userTooltip: "Doctors, Nurses & Physicians",
  },
  {
    id: "hospital",
    name: "Hospital",
    tagline: "For multi-speciality hospitals & chains",
    price_m: 24999, price_y: 249990,
    highlight: true,
    features: [
      "1 Branch · 5 Departments · 5 Admins ·\u00a0up\u00a0to\u00a020\u00a0Users", 
      "Full HMS · Pharmacy · Lab",
      "Insurance / TPA workflows",
      "Advanced analytics & exports",
      "AI Receptionist included",
      "Dedicated success manager",
    ],
    userTooltip: "Doctors, Nurses & Physicians",
  },
  {
    id: "enterprise",
    name: "Enterprise",
    tagline: "For networks, group hospitals & corporates",
    price_m: null, price_y: null,
    highlight: false,
    features: [
      "More Branches, Departments & Users",
      "All AI Suite agents included",
      "Custom integrations & SSO",
      "On-prem / private cloud option",
      "Custom SLA & compliance audits",
      "24×7 priority engineering", 
    ],
    userTooltip: "Doctors, Nurses, Physicians, Lab Technicians & more",
  },
];

const ADDONS = [
  ["AI Suite", "From ₹9,999 / month"],
  ["Telemedicine Module", "₹2,999 / branch"],
  ["Insurance & TPA", "₹3,499 / branch"],
  ["Corporate Healthcare", "Custom"],
  ["WhatsApp Business API", "Pass-through"],
  ["On-prem Deployment", "Custom"],
];

/* ── Renders feature text with "users" word as a hoverable trigger ── */
function FeatureWithUserHover({ text, tooltip, isDark }) {
  const [hovered, setHovered] = useState(false);
  const usersIndex = text.toLowerCase().indexOf("users");

  // No "users" word found → render plain
  if (usersIndex === -1) return <span>{text}</span>;

  const before = text.slice(0, usersIndex);
  const word = text.slice(usersIndex, usersIndex + 5); // "users"
  const after = text.slice(usersIndex + 5);

  return (
    <span style={{ position: "relative" }}>
      {before}
      <span
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={{
          cursor: "pointer",
          borderBottom: `1.5px dashed ${isDark ? "rgba(103,232,249,0.5)" : "rgba(233,75,44,0.45)"}`,
          paddingBottom: "1px",
          transition: "background 0.2s ease",
          borderRadius: "3px",
          background: hovered
            ? isDark ? "rgba(103,232,249,0.12)" : "rgba(233,75,44,0.08)"
            : "transparent",
        }}
      >
        {word}
      </span>
      {after}

      {/* Tooltip */}
      {hovered && (
        <span
          style={{
            position: "absolute",
            left: "0",
            top: "calc(100% + 6px)",
            zIndex: 20,
            display: "inline-flex",
            alignItems: "center",
            gap: "7px",
            padding: "9px 14px",
            borderRadius: "14px",
            whiteSpace: "nowrap",
            background: isDark
              ? "rgba(15,23,42,0.95)"
              : "linear-gradient(135deg, #FFF5F2 0%, #FFF 100%)",
            border: isDark
              ? "1px solid rgba(255,255,255,0.12)"
              : "1px solid rgba(233,75,44,0.15)",
            boxShadow: isDark
              ? "0 8px 24px rgba(0,0,0,0.4)"
              : "0 8px 24px rgba(0,0,0,0.08)",
            animation: "userTooltipIn 0.2s ease-out",
            fontSize: "13px",
            fontWeight: 700,
            color: "#E94B2C",
          }}
        >
          <Stethoscope style={{ width: 14, height: 14, flexShrink: 0 }} />
          <span>{tooltip}</span>
        </span>
      )}
    </span>
  );
}

export default function Pricing() {
  const [yearly, setYearly] = useState(true);

  return (
    <div data-testid="page-pricing">
      {/* Tooltip animation keyframe */}
      <style>{`
        @keyframes userTooltipIn {
          from { opacity: 0; transform: translateY(-4px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
      <section className="relative bg-[color:var(--paper-2)] overflow-hidden">
        <div className="absolute inset-0 bg-grid-light opacity-40" />
        <div className="relative container-x pt-4 md:pt-6 lg:pt-8 pb-12">
          <SectionHeader
            align="center"
            eyebrow="Pricing"
            title="Pay for what you run."
            titleAccent="Scale when you grow."
            subtitle="Transparent monthly & annual plans. Modular add-ons. No setup fee. Cancel anytime."
          />

          <div className="mt-10 flex items-center justify-center gap-3">
            <button
              data-testid="billing-monthly"
              onClick={() => setYearly(false)}
              className={`px-5 py-2 rounded-full text-sm font-semibold transition ${!yearly ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-700"}`}
            >Monthly</button>
            <button
              data-testid="billing-yearly"
              onClick={() => setYearly(true)}
              className={`px-5 py-2 rounded-full text-sm font-semibold transition ${yearly ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-700"}`}
            >Yearly <span className="ml-1 text-[color:var(--brand)]">save 2 months</span></button>
          </div>
        </div>
      </section>

      <section className="pb-24 bg-white">
        <div className="container-x grid lg:grid-cols-3 gap-6">
          {PLANS.map((p) => (
            <div
              key={p.id}
              data-testid={`plan-${p.id}`}
              className={`relative rounded-3xl p-8 transition-all ${
                p.highlight
                  ? "bg-[#0F172A] text-white shadow-2xl scale-[1.02]"
                  : "bg-white border border-slate-200"
              }`}
            >
              {p.highlight && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-[color:var(--brand)] text-white text-xs font-bold uppercase tracking-wider flex items-center gap-1">
                  <Sparkles className="w-3 h-3" /> Most Popular
                </div>
              )}
              <div className="flex items-center justify-between">
                <h3 className="text-2xl font-bold" style={{ fontFamily: "'Outfit',sans-serif" }}>{p.name}</h3>
                {p.highlight && <Zap className="w-5 h-5 text-[color:var(--brand)]" />}
              </div>
              <p className={`mt-1 text-sm ${p.highlight ? "text-slate-300" : "text-slate-500"}`}>{p.tagline}</p>

              <div className="mt-6">
                {p.price_m === null ? (
                  <div className="text-4xl font-bold" style={{ fontFamily: "'Outfit',sans-serif" }}>Custom</div>
                ) : (
                  <div className="flex items-baseline gap-1">
                    <span className="text-4xl font-bold" style={{ fontFamily: "'Outfit',sans-serif" }}>
                      ₹{(yearly ? p.price_y : p.price_m).toLocaleString("en-IN")}
                    </span>
                    <span className={`text-sm ${p.highlight ? "text-slate-400" : "text-slate-500"}`}>/{yearly ? "year" : "month"}</span>
                  </div>
                )}
              </div>

              <ul className="mt-6 space-y-3">
                {p.features.map((f) => (
                  <li key={f} className="flex gap-2.5 text-sm">
                    <Check className={`w-4 h-4 mt-0.5 flex-shrink-0 ${p.highlight ? "text-cyan-300" : "text-[color:var(--brand)]"}`} />
                    <span className={p.highlight ? "text-slate-200" : "text-slate-700"}>
                      {f.toLowerCase().includes("users") ? (
                        <FeatureWithUserHover text={f} tooltip={p.userTooltip} isDark={p.highlight} />
                      ) : (
                        f
                      )}
                    </span>
                  </li>
                ))}
              </ul>

              <div className="mt-7 flex flex-col gap-3">
                {/*
                {p.id === "enterprise" ? (
                  <Link to="/contact" className="btn-dark w-full justify-center">Talk to Sales <ArrowUpRight className="w-4 h-4" /></Link>
                ) : (
                  <Link to="/request-demo" className={`${p.highlight ? "btn-primary" : "btn-ghost"} w-full justify-center`} data-testid={`plan-${p.id}-cta`}>
                    Start with {p.name} <ArrowUpRight className="w-4 h-4" />
                  </Link>
                )}
                */}
              </div>
            </div>
          ))}
        </div>

        {/* Add-ons */}
        <div className="container-x mt-20">
          <SectionHeader
            eyebrow="Optional Add-ons"
            title="Mix and match"
            titleAccent="exactly what your facility needs."
          />
          <div className="mt-10 grid md:grid-cols-3 gap-4">
            {ADDONS.map(([t, p]) => (
              <div key={t} className="rounded-2xl border border-slate-200 p-5 flex items-center justify-between hover:border-[color:var(--brand)] transition">
                <div className="font-semibold">{t}</div>
                <div className="text-sm text-slate-500">{p}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
